'use client';

import React from 'react';
import {
  Car,
  Briefcase,
  ShieldCheck,
  Plane,
  Clock,
  Sparkles,
  ArrowRight,
  Headphones
} from 'lucide-react';

interface ServicesViewProps {
  onBookService: () => void;
}

export function ServicesView({ onBookService }: ServicesViewProps) {
  const services = [
    {
      icon: Car,
      title: 'Daily & Weekend Car Rental',
      desc: 'Transparent daily rates with zero hidden charges, unlimited mileage, and instant online check-in so you can drive away in under 3 minutes.',
    },
    {
      icon: Briefcase,
      title: 'Corporate Fleet Management',
      desc: 'Dedicated account managers, consolidated monthly billing, customized company insurance, and flexible swap privileges for business teams.',
    },
    {
      icon: Clock,
      title: 'Long-Term & Monthly Subscription',
      desc: 'Enjoy all the freedom of having a car without long leases, depreciation, or maintenance hassles. Swap vehicles as your lifestyle changes.',
    },
    {
      icon: Plane,
      title: 'Airport VIP Curbside Delivery',
      desc: 'Land and step directly into your vehicle at the terminal curb. No rental shuttles or counter lines required.',
    },
    {
      icon: ShieldCheck,
      title: 'Comprehensive Shield Coverage',
      desc: 'Zero-deductible CDW protection, glass and tire coverage, and 24/7 roadside assistance across the entire USA and Canada.',
    },
    {
      icon: Headphones,
      title: '24/7 Concierge & Roadside Support',
      desc: 'Our multilingual support dispatchers are always available to help with itinerary questions, route recommendations, or roadside needs.',
    },
  ];

  return (
    <div className="w-full bg-slate-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="mb-10">
          <span className="text-xs font-black tracking-wider uppercase text-[var(--color-primary)] block mb-1">
            WHAT WE OFFER
          </span>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            fleetava Premium Mobility Services
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-xl">
            From flexible daily rentals to corporate fleet provisioning and long-term passes, explore how we power your journeys.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((svc, idx) => {
            const Icon = svc.icon;
            return (
              <div
                key={idx}
                className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xs hover:shadow-lg transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-[var(--color-primary)] flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-extrabold text-slate-900 tracking-tight mb-2">
                    {svc.title}
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {svc.desc}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={onBookService}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] cursor-pointer"
                  >
                    <span>Inquire / Book Service</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
